// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useSessionTimeout from "@pace/App/Hooks/useSessionTimeout";
import { xIsAuthenticated } from "@pace/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@pace/App/Store/Selectors/SettingsSelectors";
import Button from "@pace/MobiusOverrides/Button/Button";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps> & HasHistory;

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    siteTimeoutMinutes: getSettingsCollection(state).generalSettings.siteTimeoutMinutes,
    signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    location: getLocation(state),
});

export interface SessionTimeoutModalStyles {
    modal?: ModalPresentationProps;
    buttonsWrapper?: InjectableCss;
}

export const sessionTimeoutModalStyles: SessionTimeoutModalStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            margin-top: 20px;
        `,
    },
};

const SessionTimeoutModal = ({ isAuthenticated, siteTimeoutMinutes, signInPageUrl, location, history }: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);

    if (!isAuthenticated) {
        return null;
    }

    useSessionTimeout(siteTimeoutMinutes, () => {
        setModalIsOpen(true);
    });

    const redirectToSignIn = () => {
        if (!signInPageUrl) {
            history.push("/MyAccount/SignIn");
        } else {
            const returnUrl = location.pathname + location.search;
            if (returnUrl === "/") {
                history.push(`${signInPageUrl}`);
            } else {
                history.push(`${signInPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
            }
        }
    };

    return (
        <Modal
            {...sessionTimeoutModalStyles.modal}
            headline={siteMessage("Custom_Session_Timeout_Modal_Title")}
            isOpen={modalIsOpen}
            handleClose={() => setModalIsOpen(false)}
            sizeVariant="small"
        >
            <Typography>{siteMessage("Custom_Session_Timeout_Modal_Message")}</Typography>
            <StyledWrapper {...sessionTimeoutModalStyles.buttonsWrapper}>
                <Button
                    onClick={() => setModalIsOpen(false)}
                    variant="secondary"
                    sizeVariant="medium"
                    aria-label="Close"
                >
                    {translate("Close")}
                </Button>
                <Button onClick={redirectToSignIn} variant="primary" sizeVariant="medium" aria-label="Go To Login">
                    {translate("Sign In")}
                </Button>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps)(withHistory(SessionTimeoutModal));
